// screens/CockpitToday.tsx — B16a/B16b: the personal pack's morning screen,
// and GET /api/today's FIRST consumer (the door shipped in B4c and had zero
// clients until this file).
//
// The four receipt groups, in priority order: WAITING ON ME (threads + open
// promises, overdue marked) · ARRIVED (with the window STATED — "since when"
// comes from the server, never assumed) · WAITING ON THEM · GONE QUIET.
// KEPT renders last and small: the receipt that the system notices kept
// promises, which is the North Star's whole point.
//
// One screen, one job. The proposal/attention feed the Actor Today carries
// is deliberately NOT here — Review owns the queue (spec §B16b/B16d).
//
// EMPTY IS THE FIRST THING HUAN SEES: Eden proper reads no mailbox until
// B16e (his call), so the empty state is a designed surface, not a fallback.
// It states what the groups will hold and what real act fills them — the
// truth, once, quietly. No instructional captions (rule A4): the empty state
// teaches by naming what appears, never by explaining controls.
import { useEffect, useState } from 'preact/hooks';
import { AttentionTile } from '@vowric/ui';
import {
  reconnectGoogleConnection,
  ApiError,
  getToday,
  listProducts,
  listSlipping,
  type SlippingApi,
  type TodayBriefing,
  type TodayCommitmentRow,
  type TodayJudgmentRun,
  type TodayProceedingProgramme,
  type TodayReceipt,
  type TodayThreadRow,
} from '../lib/api';
import { proposalKindLabel, proposalSummaryLine } from '../lib/proposals';
import { meterSentence } from '../lib/meterSentence';
import { GlanceHeader } from '../components/GlanceHeader';
import { threadStateLabel } from './Threads';
import './CockpitToday.css';

export interface CockpitTodayProps {
  tenantId: string;
  userName: string;
  onOpenThread: (threadId: string) => void;
  /** Chunk TODAY-W — judgment/proceeding/receipts rows carry plain paths
   * from the server (`/delivery/programmes/:id`, `/review`); this is the
   * same raw-path navigator RexxHome/TechToday already take. */
  onNavigate: (path: string) => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; briefing: TodayBriefing };

function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return `${Math.max(1, Math.floor(ms / 60_000))}m`;
  if (h < 48) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function windowLabel(window: { hours: number; since: string }): string | null {
  // The server states the window ({ hours, since } — core/party/today.ts's
  // own shape). Rendered, never assumed: an empty Arrived means "nothing in
  // this window", and the reader deserves to know which window.
  const since = (window as { since?: string }).since;
  if (typeof since !== 'string') return null;
  const d = new Date(since);
  if (Number.isNaN(d.getTime())) return null;
  return `since ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

function ThreadRow({ t, onOpen, productName }: {
  t: TodayThreadRow;
  onOpen: (id: string) => void;
  productName: (id: string | null) => string | null;
}) {
  // B16b: the filing rides the row — party, and the product chip when the
  // thread is filed to one.
  const product = t.productId ? productName(t.productId) : null;
  return (
    <button type="button" class="ctoday__row" onClick={() => onOpen(t.threadId)}>
      <span class="ctoday__row-main">
        <span class="ctoday__row-subject">{t.subject ?? '(no subject)'}</span>
        <span class="ctoday__row-who">
          {t.party?.displayName ?? ''}
          {product ? <span class="ctoday__row-product">{product}</span> : null}
        </span>
      </span>
      <span class="ctoday__row-meta">
        <span class="ctoday__row-state">{threadStateLabel(t.state)}</span>
        <span class="ctoday__row-time">{relativeTime(t.lastMessageAt)}</span>
      </span>
    </button>
  );
}

function CommitmentRow({ c }: { c: TodayCommitmentRow }) {
  return (
    <div class={`ctoday__row ctoday__row--commitment${c.overdue ? ' is-overdue' : ''}`}>
      <span class="ctoday__row-main">
        {/* Never colour alone: the overdue mark is a stripe (CSS) + the word. */}
        <span class="ctoday__row-subject">
          {c.overdue ? <span class="ctoday__overdue-word">overdue · </span> : null}
          {c.title}
        </span>
        <span class="ctoday__row-who">{c.counterpartyName ?? ''}</span>
      </span>
      <span class="ctoday__row-meta">
        <span class="ctoday__row-time">
          {c.dueAt ? new Date(c.dueAt).toLocaleDateString([], { month: 'short', day: 'numeric' }) : ''}
        </span>
      </span>
    </div>
  );
}

// ── Chunk TODAY-W ──────────────────────────────────────────────────────

function Tile({ label, value, sub, onOpen }: { label: string; value: string; sub: string; onOpen: () => void }) {
  return (
    <button type="button" class="ctoday__tile" onClick={onOpen}>
      <div class="ctoday__tile-label">{label}</div>
      <div class="ctoday__tile-value">
        {value} <span class="ctoday__tile-sub">{sub}</span>
      </div>
    </button>
  );
}

function JudgmentProposalRow({ p, onOpen }: { p: TodayBriefing['judgment']['proposals'][number]; onOpen: () => void }) {
  return (
    <button type="button" class="ctoday__row ctoday__row--judgment" onClick={onOpen}>
      <span class="ctoday__row-main">
        <span class="ctoday__row-subject">{proposalSummaryLine(p)}</span>
        <span class="ctoday__row-who">{proposalKindLabel(p.targetType)}</span>
      </span>
    </button>
  );
}

function JudgmentRunRow({ r, onOpen }: { r: TodayJudgmentRun; onOpen: () => void }) {
  return (
    <button type="button" class="ctoday__row ctoday__row--judgment" onClick={onOpen}>
      <span class="ctoday__row-main">
        <span class="ctoday__row-subject">{r.title}</span>
        <span class="ctoday__row-who">{r.programmeTitle} · {r.why}</span>
      </span>
    </button>
  );
}

function ProceedingProgrammeRow({ p, onOpen }: { p: TodayProceedingProgramme; onOpen: () => void }) {
  const dayWord = p.dayN !== null && p.dayTotal !== null ? `day ${p.dayN} of ${p.dayTotal}` : 'not yet started';
  const next = p.nextRun ? ` · next: ${p.nextRun.title}` : '';
  return (
    <button type="button" class="ctoday__row ctoday__row--proceeding" onClick={onOpen}>
      <span class="ctoday__row-main">
        <span class="ctoday__row-subject">{p.title}</span>
        <span class="ctoday__row-who">{dayWord}{next}</span>
      </span>
    </button>
  );
}

function ReceiptRow({ r, onOpen }: { r: TodayReceipt; onOpen: (() => void) | null }) {
  const time = new Date(r.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const content = (
    <>
      <span class="ctoday__receipt-time">{time}</span>
      <span class="ctoday__receipt-text">{r.text}</span>
    </>
  );
  if (onOpen) {
    return <button type="button" class="ctoday__receipt-row" onClick={onOpen}>{content}</button>;
  }
  return <div class="ctoday__receipt-row">{content}</div>;
}

function Group({
  title,
  note,
  children,
  count,
}: {
  title: string;
  note?: string | null;
  count: number;
  children: preact.ComponentChildren;
}) {
  if (count === 0) return null;
  return (
    <section class="ctoday__group">
      <h2 class="ctoday__group-title">
        {title}
        {note ? <span class="ctoday__group-note"> {note}</span> : null}
      </h2>
      {children}
    </section>
  );
}

export function CockpitToday({ tenantId, userName, onOpenThread, onNavigate }: CockpitTodayProps) {
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [productNames, setProductNames] = useState<Map<string, string>>(new Map());
  // B18g — promises whose window has passed while the work is still held.
  const [slipping, setSlipping] = useState<SlippingApi[]>([]);

  useEffect(() => {
    let cancelled = false;
    getToday(tenantId)
      .then((briefing) => { if (!cancelled) setState({ phase: 'ready', briefing }); })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          phase: 'error',
          message: err instanceof ApiError ? err.message : 'the briefing could not be read',
        });
      });
    // ⚑ B18g — a drifting promise must not take Today down. Same posture as
    // the product chips below: the newest read on this screen is the least
    // load-bearing, and everything else here is what Huan came for.
    void listSlipping(tenantId)
      .then((drifting) => { if (!cancelled) setSlipping(drifting); })
      .catch(() => { /* the briefing stands without it */ });
    // The chips' vocabulary; a failed read costs the chips, never the screen.
    void listProducts(tenantId)
      .then((products) => { if (!cancelled) setProductNames(new Map(products.map((p) => [p.id, p.name]))); })
      .catch(() => { /* rows render without chips */ });
    return () => { cancelled = true; };
  }, [tenantId]);

  const productName = (pid: string | null) => (pid ? productNames.get(pid) ?? null : null);

  if (state.phase === 'loading') {
    // Loading shaped like the answer: group-sized blocks, not a spinner.
    return (
      <div class="ctoday">
        <GlanceHeader name={userName} />
        <div class="ctoday__eyebrow">Today</div>
        <div class="ctoday__skeleton" aria-hidden="true">
          <div class="ctoday__skeleton-block" />
          <div class="ctoday__skeleton-block" />
        </div>
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div class="ctoday">
        <GlanceHeader name={userName} />
        <div class="ctoday__eyebrow">Today</div>
        <div class="ctoday__say">
          The briefing could not be read.
          <br />
          <span class="ctoday__say-soft">{state.message}</span>
        </div>
      </div>
    );
  }

  const b = state.briefing;
  const total =
    b.arrived.length +
    b.waitingOnMe.threads.length + b.waitingOnMe.commitments.length +
    b.waitingOnThem.threads.length + b.waitingOnThem.commitments.length +
    b.goneQuiet.length +
    b.kept.repliedThreads.length + b.kept.commitments.length +
    // Chunk TODAY-W — a tenant with a running programme and no mail yet
    // must not read "nothing needs you": these count too.
    b.judgment.proposals.length + b.judgment.runs.length +
    b.proceeding.programmes.length + b.proceeding.agents.length +
    b.receipts.length + b.meters.facts.length +
    // G1 — a Google account that stopped syncing is something that needs you.
    (b.reconnect?.length ?? 0);

  // ⚑ B18g — A DRIFTING PROMISE COUNTS AS SOMETHING. This guard was written
  // before slippage existed, and without `slipping.length` it answers
  // "nothing needs you" while clients wait past dates we gave them. The THIRD
  // empty-state guard tonight to hide a new kind of content (the calendar's
  // hid holds; Actor's Today hid these): a guard that ENUMERATES what counts
  // goes stale every time a surface grows a new row, and it fails in the
  // worst direction — silently, by showing a calm screen.
  if (total === 0 && b.collisions.length === 0 && slipping.length === 0) {
    // The designed empty. This instance reads no mailbox until that is
    // switched on — the one place that dependency is allowed to be visible.
    return (
      <div class="ctoday">
        <GlanceHeader name={userName} />
        <div class="ctoday__eyebrow">Today</div>
        <div class="ctoday__say">
          Nothing needs you.
          <br />
          <span class="ctoday__say-soft">
            When mail flows and programmes run, mornings start here: what arrived,
            what waits on you, what waits on them, what went quiet, what a programme
            or an agent is proceeding with — and what was kept.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div class="ctoday">
      <GlanceHeader name={userName} />
      <div class="ctoday__eyebrow">Today</div>

      {/* Chunk TODAY-W — the plate's three tiles: week hours, decisions
          waiting, agents running. */}
      <div class="ctoday__tiles">
        <Tile
          label="Week"
          value={b.meters.hoursThisWeek !== null ? `${b.meters.hoursThisWeek}` : '—'}
          sub={`of ${b.meters.weekHours} h`}
          onOpen={() => onNavigate('/delivery')}
        />
        <Tile
          label="Decisions"
          value={`${b.judgment.proposalsTotal}`}
          sub="waiting in Review"
          onOpen={() => onNavigate('/review')}
        />
        <Tile
          label="Agents"
          value={`${b.proceeding.agents.length}`}
          sub="running"
          onOpen={() => onNavigate('/delivery')}
        />
      </div>

      {/* G1 — never a silent stale sync: a Google account Google stopped
          honouring says so here, with the one tap that fixes it. */}
      <Group title="Needs reconnecting" count={b.reconnect?.length ?? 0}>
        {(b.reconnect ?? []).map((c) => (
          <button
            type="button"
            class="ctoday__row ctoday__row--reconnect"
            key={`reconnect:${c.id}`}
            onClick={() => {
              void reconnectGoogleConnection(tenantId, c.id).then(
                (path) => window.location.assign(path),
                () => onNavigate('/personal/accounts')
              );
            }}
          >
            <span class="ctoday__row-main">
              <span class="ctoday__row-subject">Reconnect {c.email}</span>
              <span class="ctoday__row-who">{c.reason ?? 'Google stopped accepting this connection'}</span>
            </span>
          </button>
        ))}
      </Group>

      <Group title="Needs your judgment" count={b.judgment.proposals.length + b.judgment.runs.length}>
        {b.judgment.proposals.map((p) => (
          <JudgmentProposalRow p={p} onOpen={() => onNavigate('/review')} key={`proposal:${p.id}`} />
        ))}
        {b.judgment.runs.map((r) => (
          <JudgmentRunRow r={r} onOpen={() => onNavigate(r.link)} key={`run:${r.id}`} />
        ))}
        {b.judgment.proposalsTotal > b.judgment.proposals.length ? (
          <button type="button" class="ctoday__more" onClick={() => onNavigate('/review')}>
            {b.judgment.proposalsTotal - b.judgment.proposals.length} more in Review
          </button>
        ) : null}
      </Group>

      <Group title="Proceeding within authority" count={b.proceeding.programmes.length + b.proceeding.agents.length}>
        {b.proceeding.programmes.map((p) => (
          <ProceedingProgrammeRow p={p} onOpen={() => onNavigate(p.link)} key={p.id} />
        ))}
        {b.proceeding.agents.map((f) => (
          <div class="ctoday__row ctoday__row--proceeding" key={`${f.section}:${f.key}`}>
            <span class="ctoday__row-main">
              <span class="ctoday__row-subject">{f.label}</span>
              <span class="ctoday__row-who">{f.detail ?? f.state}</span>
            </span>
          </div>
        ))}
      </Group>

      <Group title="Receipts" count={b.receipts.length}>
        <div class="ctoday__receipts">
          {b.receipts.map((r, i) => (
            <ReceiptRow r={r} onOpen={r.link ? () => onNavigate(r.link!) : null} key={`${r.at}:${i}`} />
          ))}
        </div>
      </Group>

      {b.meters.facts.length > 0 ? (
        <section class="ctoday__group ctoday__group--meters">
          <h2 class="ctoday__group-title">Meters</h2>
          {b.meters.facts.map((f) => (
            // Never colour alone: the cap/stale state is the sentence itself.
            <div class="ctoday__meter-line" key={`${f.section}:${f.key}`}>{meterSentence(f)}</div>
          ))}
        </section>
      ) : null}

      {/* ⚑ B18g — WE SAID WE WOULD, AND WE HAVE NOT. A thing to KNOW rather
          than a thing to do: no tap target, the word carrying the meaning
          rather than a colour, and above the groups because a promise past
          its date outranks a thread that arrived this morning. */}
      {slipping.length > 0 ? (
        <div class="ctoday__slipping">
          <div class="ctoday__slipping-word">promised by now</div>
          {slipping.slice(0, 3).map((s) => (
            <div class="ctoday__slipping-row" key={s.hold.id}>
              <span class="ctoday__slipping-label">{s.hold.label}</span>
              <span class="ctoday__slipping-days">
                {s.daysOver === 1 ? 'a day over' : `${s.daysOver} days over`}
              </span>
            </div>
          ))}
          {slipping.length > 3 ? (
            <div class="ctoday__slipping-more">and {slipping.length - 3} more</div>
          ) : null}
        </div>
      ) : null}

      {b.collisions.length > 0 ? (
        <section class="ctoday__group ctoday__group--collisions">
          {b.collisions.map((c) => (
            // The trust primitive, not a bespoke row: severity is stripe +
            // icon + word together. Seller-side only by construction — the
            // API already gates collisions behind the tenant membership.
            <AttentionTile
              key={`${c.kind}:${c.value}`}
              severity="warning"
              icon="⇄"
              title={`${c.kind}: ${c.value}`}
              sub={`${c.previousHolderName ?? '?'} → ${c.currentHolderName ?? '?'}`}
            />
          ))}
        </section>
      ) : null}

      <Group title="Waiting on me" count={b.waitingOnMe.threads.length + b.waitingOnMe.commitments.length}>
        {b.waitingOnMe.commitments.map((c) => <CommitmentRow c={c} key={c.id} />)}
        {b.waitingOnMe.threads.map((t) => <ThreadRow t={t} onOpen={onOpenThread} productName={productName} key={t.threadId} />)}
      </Group>

      <Group title="Arrived" note={windowLabel(b.window)} count={b.arrived.length}>
        {b.arrived.map((t) => <ThreadRow t={t} onOpen={onOpenThread} productName={productName} key={t.threadId} />)}
      </Group>

      <Group title="Waiting on them" count={b.waitingOnThem.threads.length + b.waitingOnThem.commitments.length}>
        {b.waitingOnThem.commitments.map((c) => <CommitmentRow c={c} key={c.id} />)}
        {b.waitingOnThem.threads.map((t) => <ThreadRow t={t} onOpen={onOpenThread} productName={productName} key={t.threadId} />)}
      </Group>

      <Group title="Gone quiet" count={b.goneQuiet.length}>
        {b.goneQuiet.map((t) => (
          <div class="ctoday__quiet-wrap" key={t.threadId}>
            <ThreadRow t={t} onOpen={onOpenThread} productName={productName} />
            {t.hoursSinceLastMessage !== null && t.quietAfterHours !== null ? (
              // The wait explains itself: both numbers, per the API's design.
              <span class="ctoday__quiet-note">
                quiet {Math.round(t.hoursSinceLastMessage)}h · limit {t.quietAfterHours}h
              </span>
            ) : null}
          </div>
        ))}
      </Group>

      {b.kept.repliedThreads.length + b.kept.commitments.length > 0 ? (
        <section class="ctoday__group ctoday__group--kept">
          <h2 class="ctoday__group-title">Kept</h2>
          {b.kept.commitments.map((c) => (
            <div class="ctoday__kept-line" key={c.id}>{c.title}</div>
          ))}
          {b.kept.repliedThreads.map((t) => (
            <div class="ctoday__kept-line" key={t.threadId}>replied — {t.subject ?? '(no subject)'}</div>
          ))}
        </section>
      ) : null}
    </div>
  );
}
